import { APP_BRAND } from '@/config/brand';
import { getBrandConfig } from '@/config/brandConfig';
import { AssetIcon, type AssetIconProps } from './AssetIcon';

export type TickBadgeIconProps = Omit<AssetIconProps, 'src' | 'alt'> & {
  /** Success banners use a green outline check-circle; checkboxes keep the brand badge tile. */
  variant?: 'badge' | 'success';
};

function SuccessCheckCircleIcon({
  size,
  className,
  ...props
}: Omit<TickBadgeIconProps, 'variant'>) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 22 22"
      fill="none"
      aria-hidden
      {...props}
    >
      <circle cx="11" cy="11" r="9.25" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M7 11.5L9.8 14.3L15.5 8.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Rounded tick badge — AG green circle, HID #0057B7 tile + white checkmark. */
export function TickBadgeIcon({
  size = 22,
  className,
  variant = 'badge',
  ...props
}: TickBadgeIconProps) {
  if (variant === 'success') {
    return <SuccessCheckCircleIcon size={size} className={className} {...props} />;
  }

  const brandConfig = getBrandConfig();

  if (APP_BRAND === 'hid' && brandConfig.hidTickBadgeIcon) {
    return (
      <AssetIcon
        src={brandConfig.hidTickBadgeIcon}
        size={size}
        className={className}
        alt=""
        aria-hidden
        {...props}
      />
    );
  }

  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 18 18"
      fill="none"
      aria-hidden
      {...props}
    >
      <circle cx="9" cy="9" r="9" fill="var(--color-success)" />
      <path
        d="M5.5 9.5L7.5 11.5L12.5 6.5"
        stroke="var(--color-text-inverse)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
